/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { ActionKernelType } from "../../model/types";
import type { IPlayable } from "../../model/videoplayer/interfaces";
import type { TSeekRange } from "../../model/videoplayer/types";
import {
  MetadataState,
  PlayerState,
  SessionState
} from "../../model/videoplayer/enums";
import type { TrickPlayMode } from "../../model/videoplayer/trickplay";
import type { Direction, SpeedLevel } from "../../model/videoplayer/trickplay";
import type { ITrickPlayEligibility } from "../../model/videoplayer/interfaces";
import { DEFAULT_ELIGIBILITY } from "../../model/videoplayer/trickplay";

/**
 * Redux state for the VideoPlayerUI feature slice.
 *
 * Holds playback telemetry (polled from VideoPlaybackController),
 * airing metadata (derived from AiringPreviewUtils), trick-play
 * state (active flag, mode, target position), and playable metadata.
 *
 * Controls visibility (isVisible / isFading) is a pure UI concern
 * and remains in the View-layer useControlsAutoHide hook.
 */
export interface VideoPlayerUiState {
  /** Current playback time in seconds. */
  currentTime: number;
  /** Total duration in seconds. */
  duration: number;
  /** Current player state enum value. */
  playerState: PlayerState;
  /** Seek range from the player (start, end, duration). */
  seekRange: TSeekRange;
  /** Formatted airing start time label (e.g. "9:30 PM"). */
  startTimeLabel: string;
  /** Formatted airing end time label (e.g. "10:30 PM"). */
  endTimeLabel: string;
  /** Airing start time as epoch seconds. */
  airingStartEpoch: number;
  /** Airing end time as epoch seconds. */
  airingEndEpoch: number;
  /** Whether trick play is currently active. */
  isTrickPlayActive: boolean;
  /** Current trick play mode (null if inactive). */
  trickPlayMode: TrickPlayMode | null;
  /** Target seek position in seconds during trick play. */
  targetPosition: number;
  /** Currently playing content playable (null if none). */
  playable: IPlayable | null;
  /** Current seek direction during a D-pad skip (null when idle). */
  seekDirection: Direction | null;
  /** Monotonic counter incremented on each D-pad skip. */
  skipCounter: number;
  /** Current speed level during an active hold / FF / RW session. */
  currentSpeedLevel: SpeedLevel | null;
  /** Whether the press that triggered the speed level was a long press. */
  speedTriggeredByLongPress: boolean;
  /** Active trick-play eligibility (PRD §5 + restriction API). */
  trickPlayEligibility: ITrickPlayEligibility;
  /** Whether the restriction API has granted trick-play (PRD §7.1 TP-AP-1). */
  trickPlayRestrictionEnabled: boolean;
  /** Seek entry point in seconds (player position when session began). */
  seekEntryPoint: number | null;
  /** Currently playing content parental control rating. */
  parentalControlsRating: ReadonlyArray<Record<string, unknown>>;
  /** Program Title of tuned channel */
  programDetails: Array<string>;
  /** Last metadata state emitted by VideoPlaybackController. */
  metadataState: MetadataState;
  /** Whether current playback is a Start Over (SOCU) stream. */
  isStartOverPlaying: boolean;
  /**
   * Whether the playback position has settled (first frame produced). False
   * from startup until the first frame — for Start Over this is where the
   * resume seek is applied. The seek-bar knob, current-time label, and
   * thumbnail are withheld until this is true so they do not flash at a
   * transient position. Applies to all playback types.
   */
  isPositionSettled: boolean;
  /** Available audio tracks for the current source, as display options. */
  audioTracks: ReadonlyArray<AudioTrackUiOption>;
  /** Id of the currently active audio track, or null when none is active. */
  selectedAudioTrackId: number | null;
  /**
   * Whether the audio/text track metadata has finished loading for the
   * current tune. False from LOADING until LOADED/FIRST_FRAME — the
   * audio/subtitles action button is disabled until this is true, since
   * VideoPlaybackController.getAudioTrackOptions()/getTextTrackOptions()
   * can return stale or empty data before then.
   */
  areMediaTracksReady: boolean;
  /** One-shot flag: the user opened the mini-player while Guide was the
   *  playback origin. Drives the PiG↔PiP bridge and is preserved across
   *  videoPlayerUiReset. */
  wasPiGEnteredFromMiniClick: boolean;
  /** Action kernel types derived from the latest metadata actions list. */
  actionKernelTypes: ReadonlyArray<ActionKernelType>;
  /** OnePass create action info from the latest metadata actions list, or null if absent. */
  onePassActionInfo: TOnePassActionInfo | null;
  /**
   * OnePass modify action info (the series already has a OnePass) from the
   * latest metadata actions list, or null if absent or carrying no collection.
   */
  onePassModifyInfo: TOnePassActionInfo | null;
  /**
   * Collection id carried by the modify-recording action, or null when that
   * action is absent or carries none. Drives where Modify recording goes: the
   * manage-recordings screen needs this id, and without it the only sensible
   * destination is the My Hub landing screen.
   */
  modifyRecordingCollectionId: string | null;
  /**
   * One-shot flag: the model has requested a return to the previous screen
   * after a long background (Catchup / nDVR / VOD). The View performs the
   * navigation and clears it via exitToPreviousScreenHandled.
   */
  shouldExitToPreviousScreen: boolean;
  /** Latest session state emitted by VideoPlaybackController. */
  sessionState: SessionState;
  /**
   * Whether standalone media (an EAS alert tone and similar) is playing.
   * Independent of {@link playerState}, which tracks main playback only.
   */
  isStandalonePlaybackActive: boolean;
  /**
   * One-shot flag raised when standalone media reaches end of media, or fails
   * in a way that means it never will. Cleared by standalonePlaybackEndHandled
   * once the View has acted on it.
   */
  hasStandalonePlaybackEnded: boolean;
  /** Description of the last standalone playback failure; null when none. */
  standalonePlaybackError: string | null;
}

/**
 * Serializable audio-track descriptor for the selection UI.
 */
export interface AudioTrackUiOption {
  /** Unique identifier for the audio track. */
  id: number;
  /** Human-readable label shown to the user. */
  label: string;
}

export const INITIAL_STATE: VideoPlayerUiState = {
  currentTime: 0,
  duration: 0,
  playerState: PlayerState.IDLE,
  seekRange: { start: 0, end: 0, duration: 0 },
  startTimeLabel: "",
  endTimeLabel: "",
  airingStartEpoch: 0,
  airingEndEpoch: 0,
  isTrickPlayActive: false,
  trickPlayMode: null,
  targetPosition: 0,
  playable: null,
  seekDirection: null,
  skipCounter: 0,
  currentSpeedLevel: null,
  speedTriggeredByLongPress: false,
  trickPlayEligibility: DEFAULT_ELIGIBILITY,
  trickPlayRestrictionEnabled: false,
  seekEntryPoint: null,
  parentalControlsRating: [],
  programDetails: [""],
  metadataState: MetadataState.NONE,
  isStartOverPlaying: false,
  isPositionSettled: true,
  audioTracks: [],
  selectedAudioTrackId: null,
  areMediaTracksReady: false,
  wasPiGEnteredFromMiniClick: false,
  actionKernelTypes: [],
  shouldExitToPreviousScreen: false,
  sessionState: SessionState.NONE,
  onePassActionInfo: null,
  onePassModifyInfo: null,
  modifyRecordingCollectionId: null,
  isStandalonePlaybackActive: false,
  hasStandalonePlaybackEnded: false,
  standalonePlaybackError: null
};

/** Payload for the standalonePlaybackStateUpdated reducer. */
export interface StandalonePlaybackStatePayload {
  /** Lifecycle state: PLAYING, ENDED (end of media) or ERROR. */
  state: PlayerState;
  /** Failure description; present only for ERROR. */
  errorMessage?: string;
}

/** Payload for the sessionStateUpdated reducer. */
export interface SessionStatePayload {
  /** Latest session state. */
  sessionState: SessionState;
}

/** Payload for the playbackStateUpdated reducer. */
export interface PlaybackStatePayload {
  /** Current playback time in seconds. */
  currentTime: number;
  /** Total duration in seconds. */
  duration: number;
  /** Current player state enum value. */
  playerState: PlayerState;
  /** Seek range from the player. */
  seekRange: TSeekRange;
  /** Whether current playback is a Start Over stream. */
  isStartOverPlaying: boolean;
  /** Whether the playback position has settled (see VideoPlayerUiState). */
  isPositionSettled: boolean;
}

/** Payload for the airingInfoUpdated reducer. */
export interface AiringInfoPayload {
  /** Formatted airing start time label. */
  startTimeLabel: string;
  /** Formatted airing end time label. */
  endTimeLabel: string;
  /** Airing start time as epoch seconds. */
  airingStartEpoch: number;
  /** Airing end time as epoch seconds. */
  airingEndEpoch: number;
  /** ParentalControl rating record*/
  parentalControlsRating: ReadonlyArray<Record<string, unknown>>;
  /** Formatted Program Title */
  programDetails: Array<string>;
}

/** Payload for the trickPlayStateUpdated reducer. */
export interface TrickPlayStatePayload {
  /** Whether trick play is currently active. */
  isTrickPlayActive: boolean;
  /** Current trick play mode (null if inactive). */
  trickPlayMode: TrickPlayMode | null;
  /** Target seek position in seconds during trick play. */
  targetPosition: number;
  /** Current seek direction (null when idle). */
  seekDirection: Direction | null;
  /** Current speed level (null when no hold / cycle active). */
  currentSpeedLevel: SpeedLevel | null;
  /** Seek entry point in seconds (null when idle). */
  seekEntryPoint: number | null;
}

/** Payload for the trickPlayEligibilityUpdated reducer. */
export interface TrickPlayEligibilityPayload {
  /** Active trick-play eligibility. */
  eligibility: ITrickPlayEligibility;
  /** Whether the restriction API has granted trick-play. */
  restrictionEnabled: boolean;
}

/** Payload for the playableUpdated reducer. */
export interface PlayablePayload {
  /** Currently playing playable (null if none). */
  playable: IPlayable | null;
}

/** Payload for the audioTracksUpdated reducer. */
export interface AudioTracksPayload {
  /** Available audio tracks as display options. */
  audioTracks: ReadonlyArray<AudioTrackUiOption>;
  /** Id of the currently active track, or null when none is active. */
  selectedAudioTrackId: number | null;
}

/** Ucid and title extracted from a create-OnePass metadata action. */
export interface TOnePassActionInfo {
  /** Unified Collection ID from the OnePass action kernel. */
  ucid: string;
  /** Series title from the OnePass action. */
  title: string;
}

/** Payload for actionKernelTypesUpdated reducer. */
export interface ActionKernelTypesPayload {
  /** Action kernel types derived from metadata actions list. */
  actionKernelTypes: ReadonlyArray<ActionKernelType>;
  /** OnePass create action info, or null if no create-OnePass action is present. */
  onePassActionInfo: TOnePassActionInfo | null;
  /** OnePass modify action info, or null if no usable modify-OnePass action is present. */
  onePassModifyInfo: TOnePassActionInfo | null;
  /** Collection id from the modify-recording action, or null if unavailable. */
  modifyRecordingCollectionId: string | null;
}

/** Payload for the metadataStateUpdated reducer. */
export interface MetadataStatePayload {
  /** Last metadata state emitted by VideoPlaybackController. */
  metadataState: MetadataState;
}
